import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  NgZone,
  OnDestroy,
  OnInit,
} from "@angular/core";
import {
  BehaviorSubject,
  EMPTY,
  filter,
  map,
  shareReplay,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from "rxjs";

import {
  AppSettingsService,
  SubdomainEnum,
} from "src/app/services/app-settings.service";
import { DialogService } from "src/app/services/dialog.service";
import { UnitCardService } from "src/app/services/shared/unit-card.service";
import { SnackbarService } from "src/app/services/snackbar.service";
import { StorageService } from "src/app/services/storage.service";
import UnitCardStatus from "src/app/shared/enums/unit-card/UnitCardStatus.enum";
import { BaseResponseStatus } from "src/app/shared/models/BaseResponseDto.model";
import { DynamicComponentItem } from "src/app/shared/models/dynamic-component-loader/DynamicComponentItem.model";
import { TerraUtils } from "src/app/shared/TerraUtils";
import { ConfirmDialogParams } from "../../../confirm-dialog/confirm-dialog.component";
import { UnitCardConfirmDialogContentComponent } from "../confirm-dialog-content/unit-card-confirm-dialog-content.component";
import PermissionLevel from "src/app/permission/enums/permissionLevel.enum";
import { NoPermissionAction } from "src/app/shared/directives/check-permissions.directive";
import { FinancialPermissionLevel } from "src/app/permission/enums/financialPermissionLevel.enum";
import { UnitCardPinAction } from "../unit-card-pin-dialog/UnitCardPinContext";

// declare var VGSShow: any;
declare var $: any;

declare global {
  interface Window {
    VGSShow: any;
  }
}

@Component({
  selector: "terra-unit-card-actions",
  templateUrl: "./unit-card-actions.component.html",
  styleUrls: ["./unit-card-actions.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnitCardActionsComponent implements OnInit, OnDestroy {
  loadingSensitiveData$ = this.unitCardService.loadingSensitiveData$;
  viewSensitiveData$ = this.unitCardService.viewSensitiveData$;

  hasError$ = new BehaviorSubject<boolean>(false);
  errorGeneralMessage$ = new BehaviorSubject<string>(null);
  unitBusinessCard$ = this.unitCardService.unitBusinessCard$;
  cardStatus$ = this.unitBusinessCard$.pipe(
    filter((unitBusinessCard) => !!unitBusinessCard),
    map((unitBusinessCard) => unitBusinessCard.cardStatus),
    shareReplay(1)
  );
  cardIsPinCodeExist$ = this.unitBusinessCard$.pipe(
    filter((unitBusinessCard) => !!unitBusinessCard),
    map((unitBusinessCard) => unitBusinessCard.isPinCodeExist),
    shareReplay(1)
  );
  isCancelStatus$ = this.cardStatus$.pipe(
    map(
      (status) =>
        status !== UnitCardStatus.Active && status !== UnitCardStatus.Frozen
    ),
    shareReplay(1)
  );

  UnitCardStatus = UnitCardStatus;
  PermissionLevel = PermissionLevel;
  NoPermissionAction = NoPermissionAction;
  FinancialPermissionLevel = FinancialPermissionLevel;

  show = window.VGSShow.create(
    this.appSettingService.VGSVault,
    (state: any) => {}
  );
  cvv2Iframe: any;
  copyButtonCvv: any;

  cardNumberIframe: any;
  copyButtonCardNumber: any;

  @HostBinding("class.gp-side") isLpHost =
    this.appSettingService.subDomain === SubdomainEnum.GP;

  constructor(
    private unitCardService: UnitCardService,
    private appSettingService: AppSettingsService,
    private snackbarService: SnackbarService,
    private dialogService: DialogService,
    private storageService: StorageService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone
  ) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.offVgsEvent();
  }

  private resetError() {
    this.hasError$.next(false);
    this.errorGeneralMessage$.next(null);
  }

  private setError(message: string) {
    this.hasError$.next(true);
    this.errorGeneralMessage$.next(message);
  }

  transaction() {
    this.unitBusinessCard$
      .pipe(
        take(1),
        map((unitBusinessCard) => unitBusinessCard.last4Digits)
      )
      .subscribe({
        next: (last4Digits) =>
          this.unitCardService.filterCardTransaction(`**${last4Digits}`),
        error: (err) => {},
      });
  }

  revealDetails() {
    this.resetError();
    this.isCancelStatus$
      .pipe(
        take(1),
        filter((isCancelStatus) => !isCancelStatus),
        switchMap((_) => this.unitCardService.unitCustomerToken$.pipe(take(1))),
        withLatestFrom(
          this.unitCardService.unitBusinessCard$,
          this.unitCardService.viewSensitiveData$
        )
      )
      .subscribe({
        next: ([unitCustomerToken, unitBusinessCard, viewSensitiveData]) => {
          if (viewSensitiveData) {
            this.unitCardService.resetViewSensitiveData();
          } else if (
            !!unitCustomerToken &&
            unitCustomerToken.responseStatus === BaseResponseStatus.Success
          ) {
            this.offVgsEvent();
            this.unitCardService.updateLoadingSensitiveData(true);
            this.VgsShowCardNumber(
              unitCustomerToken.token,
              unitBusinessCard.unitCardResourceId,
              unitBusinessCard.id
            );
            this.VgsShowCvv(
              unitCustomerToken.token,
              unitBusinessCard.unitCardResourceId,
              unitBusinessCard.id
            );
          } else if (
            !!unitCustomerToken &&
            unitCustomerToken.responseStatus !== BaseResponseStatus.Success
          ) {
            this.unitCardService.resetViewSensitiveData();
            this.setError("reveal details");
          }
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.unitCardService.resetViewSensitiveData();
          this.setError("reveal details");
        },
      });
  }

  edit() {}

  freeze() {
    let msg = "";
    this.unitBusinessCard$
      .pipe(
        take(1),
        switchMap((unitBusinessCard) => {
          if (unitBusinessCard.cardStatus === UnitCardStatus.Frozen) {
            msg = "un freezed";
            return this.unitCardService.unFreezeBusinessDebitCard();
          } else if (unitBusinessCard.cardStatus === UnitCardStatus.Active) {
            msg = "freezed";
            return this.freezeCardFlow();
          }
          return EMPTY;
        }),
        tap((_) => this.unitCardService.resetViewSensitiveData())
      )
      .subscribe({
        next: (unitBusinessCard) =>
          this.snackbarService.showGeneralMessage(
            `Business virtual debit card ${msg} successfully`,
            5
          ),
        error: (error) =>
          this.snackbarService.showGeneralMessage(
            `Business virtual debit card ${msg} failed`,
            5
          ),
      });
  }

  changeLimit() {
    alert("changeLimit");
  }

  setPin() {
    this.unitCardService
      .doDebitCardPinCodeAction(
        UnitCardPinAction.SET_PIN,
        "PIN code saved successfully",
        "PIN code saving failed!"
      )
      .subscribe();
  }

  resetPin() {
    this.unitCardService
      .doDebitCardPinCodeAction(
        UnitCardPinAction.RESET_PIN,
        "PIN code reset successfully",
        "PIN code reset failed!"
      )
      .subscribe();
  }

  cancelCard() {
    const confirmDialogParams = new ConfirmDialogParams();
    confirmDialogParams.title = "Cancel Card";
    confirmDialogParams.panelClass = "unit-card-cancel-card-dialog";
    confirmDialogParams.description =
      "<div>Are you sure you want to cancel the card?</div><div>This cannot be undone.</div>";
    confirmDialogParams.actionLabel = "Cancel Card";
    confirmDialogParams.actionButtonColor = "red";
    confirmDialogParams.cancelLabel = "Keep it";

    this.unitBusinessCard$
      .pipe(
        take(1),
        filter(
          (unitBusinessCard) =>
            unitBusinessCard.cardStatus !== UnitCardStatus.ClosedByCustomer
        ),
        switchMap((unitBusinessCard) => {
          const includeDynamicComponent = new DynamicComponentItem(
            UnitCardConfirmDialogContentComponent,
            {
              holderName: TerraUtils.getUnitFullName(unitBusinessCard.fullName),
              cardNumber: unitBusinessCard.last4Digits,
            }
          );

          confirmDialogParams.dynamicComponent = includeDynamicComponent;

          return this.dialogService
            .confirmDialog(confirmDialogParams)
            .afterClosed();
        }),
        filter((confirmed) => !!confirmed),
        switchMap((_) => this.unitCardService.cancelBusinessDebitCard()),
        tap((_) => this.unitCardService.resetViewSensitiveData())
      )
      .subscribe({
        next: (unitBusinessCard) =>
          this.snackbarService.showGeneralMessage(
            "Business virtual debit card canceled successfully",
            5
          ),
        error: (error) =>
          this.snackbarService.showGeneralMessage(
            "Business virtual debit card canceled failed",
            5
          ),
      });
  }

  private freezeCardFlow() {
    const confirmDialogParams = new ConfirmDialogParams();
    confirmDialogParams.title = "Freeze Card";
    confirmDialogParams.panelClass = "unit-card-freeze-card-dialog";
    confirmDialogParams.description =
      "<div>Are you sure you want to freeze the card?</div>";
    confirmDialogParams.actionLabel = "Freeze Card";
    confirmDialogParams.actionButtonColor = "red";
    confirmDialogParams.cancelLabel = "Keep it";

    return this.unitBusinessCard$.pipe(
      take(1),
      filter(
        (unitBusinessCard) =>
          unitBusinessCard.cardStatus !== UnitCardStatus.Frozen
      ),
      switchMap((unitBusinessCard) => {
        const includeDynamicComponent = new DynamicComponentItem(
          UnitCardConfirmDialogContentComponent,
          {
            holderName: TerraUtils.getUnitFullName(unitBusinessCard.fullName),
            cardNumber: unitBusinessCard.last4Digits,
          }
        );

        confirmDialogParams.dynamicComponent = includeDynamicComponent;

        return this.dialogService
          .confirmDialog(confirmDialogParams)
          .afterClosed();
      }),
      filter((confirmed) => !!confirmed),
      switchMap((_) => this.unitCardService.freezeBusinessDebitCard())
    );
  }

  private snackbarMessage(itemName: string) {
    if (!!itemName) {
      this.snackbarService.showGeneralMessage(
        `${itemName} was copied to the clipboard`,
        3
      );
    }
  }

  private VgsShowCardNumber(
    customerToken: string,
    unitCardResourceId: string,
    cardId: number
  ) {
    document.getElementById(`secured-card-number-${cardId}`).innerHTML = "";
    (
      document.getElementById(
        `card-number-copy-button-${cardId}`
      ) as HTMLElement
    ).style.display = "none";

    const copyBtnCss = {
      width: TerraUtils.isMobileDevice() ? "145px" : "160px",
      height: "calc(1.5625rem)",
      position: "relative",
      display: "flex",
      "-webkit-box-align": "center",
      "align-items": "center",
      "outline-width": "0px",
      "border-width": "0px",
      "justify-content": "end",
      cursor: "pointer",

      "&:hover::after": {
        "--copy-opacity": 1,
        cursor: "pointer",
      },
      "::after": {
        content: `url("${this.appSettingService.gpFrontEnvironmentAppBaseUrl}assets/images/unit-card/copy-icon.svg")`,
        position: "absolute",
        transition: "opacity 0.15s ease-in-out 0s",
        opacity: TerraUtils.isMobileDevice() ? "1" : "var(--copy-opacity, 0)",
        "inset-inline-end": "0px",
      },
    };

    this.cardNumberIframe = this.show.request({
      name: "data-text",
      method: "GET",
      path: "/cards/" + unitCardResourceId + "/secure-data/pan",
      headers: {
        Authorization: "Bearer " + customerToken,
      },
      htmlWrapper: "text",
      jsonPathSelector: "data.attributes.pan",
      serializers: [
        this.show.SERIALIZERS.replace(
          "(\\d{4})(\\d{4})(\\d{4})(\\d{4})",
          "$1 $2 $3 $4"
        ),
      ],
    });

    this.cardNumberIframe.render(`#secured-card-number-${cardId}`, {
      "@font-face": {
        "font-family": "Roboto",
        src: 'url(https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxK.woff2) format("woff2")',
        "unicode-range":
          "U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD",
      },

      "font-family": '"Roboto", sans-serif',
      color: "white",
      "font-size": TerraUtils.isMobileDevice() ? "12px" : "13px",
      display: "flex",
      "-webkit-box-align": "center",
      "align-items": "center",
      height: "calc(1.5625rem)",
      width: TerraUtils.isMobileDevice() ? "145px" : "160px",
    });

    this.copyButtonCardNumber = this.show.copyFrom(
      this.cardNumberIframe,
      { text: "", serializers: [this.show.SERIALIZERS.replace(" ", "")] },
      (status) => {
        if (status === "success") {
          this.snackbarMessage("Card Number");
        }
      }
    );

    this.copyButtonCardNumber.render(
      `#card-number-copy-button-${cardId}`,
      copyBtnCss
    );

    this.cardNumberIframe.on("requestFail", (error) => {
      this.zone.run(() => {
        this.failVGSDataSensitive();
      });
    });

    this.cardNumberIframe.on("revealFail", () => {
      this.zone.run(() => {
        this.failVGSDataSensitive();
      });
    });

    this.cardNumberIframe.on("revealSuccess", () => {
      this.zone.run(() => {
        this.unitCardService.allowViewSensitiveData();
        this.unitCardService.updateLoadingSensitiveData(false);
        this.cardNumberIframeStyle(cardId);
      });
    });
  }

  private VgsShowCvv(
    customerToken: string,
    unitCardResourceId: string,
    cardId: number
  ) {
    document.getElementById(`secured-cvv-${cardId}`).innerHTML = "";
    (
      document.getElementById(`cvv-copy-button-${cardId}`) as HTMLElement
    ).style.display = "none";

    this.cvv2Iframe = this.show.request({
      name: "data-text",
      method: "GET",
      path: "/cards/" + unitCardResourceId + "/secure-data/cvv2",
      headers: {
        Authorization: "Bearer " + customerToken,
      },
      htmlWrapper: "text",
      jsonPathSelector: "data.attributes.cvv2",
    });

    this.cvv2Iframe.render(`#secured-cvv-${cardId}`, {
      "@font-face": {
        "font-family": "Roboto",
        src: 'url(https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxK.woff2) format("woff2")',
        "unicode-range":
          "U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD",
      },

      "font-family": '"Roboto", sans-serif',
      width: "calc(2.5rem)",
      display: "flex",
      "align-items": "center",
      "-webkit-box-align": "center",
      height: "calc(1.5625rem)",
      color: "white",
      "font-size": TerraUtils.isMobileDevice() ? "12px" : "13px",
    });

    const copyBtnCss = {
      width: TerraUtils.isMobileDevice() ? "calc(2.6rem)" : "calc(3rem)",
      height: "calc(1.5625rem)",
      position: "relative",
      display: "flex",
      "-webkit-box-align": "center",
      "align-items": "center",
      "outline-width": "0px",
      "border-width": "0px",
      "justify-content": "end",
      cursor: "pointer",

      "&:hover::after": {
        "--copy-opacity": 1,
        cursor: "pointer",
      },
      "::after": {
        content: `url("${this.appSettingService.gpFrontEnvironmentAppBaseUrl}assets/images/unit-card/copy-icon.svg")`,
        position: "absolute",
        transition: "opacity 0.15s ease-in-out 0s",
        opacity: TerraUtils.isMobileDevice() ? "1" : "var(--copy-opacity, 0)",
        "inset-inline-end": "0px",
      },
    };

    this.copyButtonCvv = this.show.copyFrom(
      this.cvv2Iframe,
      { text: "" },
      (status) => {
        if (status === "success") {
          this.snackbarMessage("CVV");
        }
      }
    );

    this.copyButtonCvv.render(`#cvv-copy-button-${cardId}`, copyBtnCss);

    this.cvv2Iframe.on("requestFail", (error) => {
      this.zone.run(() => {
        this.failVGSDataSensitive();
      });
    });

    this.cvv2Iframe.on("revealFail", () => {
      this.zone.run(() => {
        this.failVGSDataSensitive();
      });
    });

    this.cvv2Iframe.on("revealSuccess", () => {
      this.zone.run(() => {
        this.unitCardService.allowViewSensitiveData();
        this.unitCardService.updateLoadingSensitiveData(false);
        this.cvvIframeStyle(cardId);
      });
    });
  }

  private offVgsEvent() {
    if (!!this.cvv2Iframe) {
      this.cvv2Iframe.off("revealSuccess");
      this.cvv2Iframe.off("requestFail");
      this.cvv2Iframe.off("revealFail");
    }

    if (!!this.cardNumberIframe) {
      this.cardNumberIframe.off("revealSuccess");
      this.cardNumberIframe.off("requestFail");
      this.cardNumberIframe.off("revealFail");
    }
  }

  private failVGSDataSensitive() {
    this.unitCardService.updateLoadingSensitiveData(false);
    this.setError("reveal details");
    this.storageService.removeUnitCustomerToken();
  }

  private cvvIframeStyle(cardId) {
    (
      document.getElementById(`secured-cvv-${cardId}`)
        .children[0] as HTMLElement
    ).style.maxHeight = "calc(1.5625rem)";
    (
      document.getElementById(`secured-cvv-${cardId}`)
        .children[0] as HTMLElement
    ).style.display = "flex";
    (
      document.getElementById(`secured-cvv-${cardId}`)
        .children[0] as HTMLElement
    ).style.maxWidth = "calc(3.125rem)";

    (
      document.getElementById(`cvv-copy-button-${cardId}`) as HTMLElement
    ).style.display = "block";
    (
      document.getElementById(`cvv-copy-button-${cardId}`)
        .children[0] as HTMLElement
    ).style.maxHeight = "calc(1.5625rem)";
    (
      document.getElementById(`cvv-copy-button-${cardId}`)
        .children[0] as HTMLElement
    ).style.maxWidth = "calc(5.125rem)";
  }

  private cardNumberIframeStyle(cardId) {
    (
      document.getElementById(`secured-card-number-${cardId}`)
        .children[0] as HTMLElement
    ).style.maxHeight = "calc(1.6rem)";
    (
      document.getElementById(`secured-card-number-${cardId}`)
        .children[0] as HTMLElement
    ).style.display = "flex";
    (
      document.getElementById(`secured-card-number-${cardId}`)
        .children[0] as HTMLElement
    ).style.maxWidth = TerraUtils.isMobileDevice() ? "145px" : "160px";

    (
      document.getElementById(
        `card-number-copy-button-${cardId}`
      ) as HTMLElement
    ).style.display = "block";
    (
      document.getElementById(`card-number-copy-button-${cardId}`)
        .children[0] as HTMLElement
    ).style.maxHeight = "calc(1.6rem)";
    (
      document.getElementById(`card-number-copy-button-${cardId}`)
        .children[0] as HTMLElement
    ).style.maxWidth = TerraUtils.isMobileDevice() ? "145px" : "160px";
  }
}
